import { createContext, useContext, useEffect, useState, useCallback } from "react";
import { useAuth } from "@/context/AuthContext";
import api from "@/lib/api";

const ThemeContext = createContext(null);

const resolveDark = (theme) =>
  theme === "dark" ||
  (theme === "system" && window.matchMedia("(prefers-color-scheme: dark)").matches);

export const ThemeProvider = ({ children }) => {
  const { user } = useAuth();
  const [theme, setThemeState] = useState(() => localStorage.getItem("theme") || "light");
  const [isDark, setIsDark] = useState(() => resolveDark(localStorage.getItem("theme") || "light"));

  const apply = useCallback((t) => {
    const dark = resolveDark(t);
    document.documentElement.classList.toggle("dark", dark);
    setIsDark(dark);
  }, []);

  useEffect(() => {
    apply(theme);
  }, [theme, apply]);

  // adopt the server-side preference when the user logs in
  useEffect(() => {
    const serverTheme = user?.preferences?.theme;
    if (serverTheme && ["light", "dark", "system"].includes(serverTheme) && serverTheme !== theme) {
      setThemeState(serverTheme);
      localStorage.setItem("theme", serverTheme);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.preferences?.theme]);

  // live-update when OS scheme changes in "system" mode
  useEffect(() => {
    const mq = window.matchMedia("(prefers-color-scheme: dark)");
    const handler = () => theme === "system" && apply("system");
    mq.addEventListener("change", handler);
    return () => mq.removeEventListener("change", handler);
  }, [theme, apply]);

  const setTheme = async (t) => {
    setThemeState(t);
    localStorage.setItem("theme", t);
    try {
      if (user) await api.put("/auth/me", { preferences: { theme: t } });
    } catch {
      // preference persists locally even if server call fails
    }
  };

  return (
    <ThemeContext.Provider value={{ theme, setTheme, isDark }}>
      {children}
    </ThemeContext.Provider>
  );
};

export const useTheme = () => useContext(ThemeContext);
